import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Button } from '../../components/ui/Button';
import { useAvatarImageUrl } from '../../components/ui/AvatarView';
import { searchTemplates, fetchTemplate } from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import type { Template } from '../../types';
import { usePersistedState, oneOf } from '../../lib/usePersistedState';

interface PickerLocationState {
  selected?: string[];
  returnTo?: string;
}

function ResultRow({ template, checked, onToggle }: { template: Template; checked: boolean; onToggle: () => void }) {
  const imageUrl = useAvatarImageUrl(template.avatarImageKey);
  return (
    <button onClick={onToggle} className="w-full text-left">
      <AppCard className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0 overflow-hidden"
          style={{ background: imageUrl ? undefined : `${template.colorHex}26` }}
        >
          {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : template.emoji}
        </div>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <span className="font-medium truncate">{template.title}</span>
          <span className="text-xs text-black/40 dark:text-white/40 truncate">{template.nickname}</span>
        </div>
        <span
          className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
            checked ? 'bg-blue-600 border-blue-600' : 'border-black/20 dark:border-white/20'
          }`}
        >
          {checked && <Check size={14} color="white" strokeWidth={3} />}
        </span>
      </AppCard>
    </button>
  );
}

// A dedicated, full page rather than the old inline single-select
// <Select> that used to live directly in ChannelManageScreen - that
// approach depended on the *entire* template index already being loaded
// client-side (fetchTemplates' fixed 20-item global fetch), which
// doesn't hold up once there can be 100k+ templates. This page instead
// searches on demand (debounced, server-paginated via
// api/dataApi.ts's searchTemplates - see backend/workflows/
// template/search.wsl and search_mine.wsl) and never assumes the full
// set fits in memory, matching how a "search and multi-select" UI needs
// to behave at that scale.
export function TemplatePickerScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as PickerLocationState;
  const returnTo = state.returnTo ?? '/channels';

  const [tab, setTab] = usePersistedState<'mine' | 'public'>('picker.templates.tab', 'mine', oneOf(['mine', 'public'] as const));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(state.selected ?? []));

  // Guards against a slower, earlier request's response overwriting a
  // faster, later one (e.g. typing quickly, or switching tabs mid-search)
  // - the classic out-of-order-response race for any debounced search box.
  const requestId = useRef(0);

  useEffect(() => {
    const thisRequest = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        // See ShowcaseScreen.tsx's identical comment - complementary to the
        // requestId guard above, not a replacement for it.
        const page = await queryClient.query({
          queryKey: queryKeys.templateSearch(tab, query, 0),
          queryFn: () => searchTemplates(tab, query),
        });
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter(
          (t): t is Template => t !== null
        );
        if (requestId.current === thisRequest) setResults(hydrated);
      } catch {
        if (requestId.current === thisRequest) setResults([]);
      } finally {
        if (requestId.current === thisRequest) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tab, query]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const done = () => {
    navigate(returnTo, { state: { selectedTemplateIds: Array.from(selected) }, replace: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Select schemas" onBack={done} />

      <SegmentedControl
        options={[
          { value: 'mine' as const, label: 'My own' },
          { value: 'public' as const, label: 'Public' },
        ]}
        value={tab}
        onChange={setTab}
      />

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={tab === 'mine' ? 'Search my schemas' : 'Search public schemas'}
        className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
      />

      <div className="flex flex-col gap-2.5 pb-20">
        {loading && <span className="text-sm text-black/40 dark:text-white/40 py-4 text-center">Searching...</span>}
        {!loading && results.length === 0 && (
          <span className="text-sm text-black/40 dark:text-white/40 py-4 text-center">
            {query ? 'No schemas match that search.' : 'No schemas yet.'}
          </span>
        )}
        {!loading &&
          results.map((t) => (
            <ResultRow key={t.id} template={t} checked={selected.has(t.id)} onToggle={() => toggle(t.id)} />
          ))}
      </div>

      {/* z-40: above the site-wide AppFooter's z-30 (App.tsx) - see
          ChannelPickerScreen.tsx's identical fix for why. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-white dark:bg-[#1c1c1e] border-t border-black/10 dark:border-white/10">
        <Button variant="primary" full onClick={done}>
          Done{selected.size > 0 ? ` (${selected.size} selected)` : ''}
        </Button>
      </div>
    </div>
  );
}
